// docs/147 PG2: Phaser 4 builds a Graphics' triangles from its commands on every frame it draws:
// each arc and ellipse walked point by point, each fill run through earcut, each stroke widened into
// quads. The board's Graphics almost never change between frames (the void's edge, fixtures' plates,
// unit rings, the shadows), so that work was most of a frame's CPU on a big board (15 ms of 30 on an
// exploration for twenty). Here each Graphics remembers the triangles its last draw handed the batch,
// with what they were made from: its commands, its transform on screen, its alpha, the camera and the
// lighting. While all of those are unchanged the triangles are handed over again as they are, the
// very same numbers; when only the camera's pan moved (the same zoom), they are handed over shifted
// by it. Anything else (a zoom, a new command, a fade) builds them afresh, exactly as before.
import Phaser from 'phaser';

/** What the batch was handed: triangles (indices into `vertices`), positions, packed colours. */
interface Batch { indexes: number[]; vertices: number[]; colors: number[]; lighting: unknown }

interface Tess {
  cmds: unknown[];
  a: number; b: number; c: number; d: number; e: number; f: number;
  alpha: number; camera: unknown; lighting: unknown; detail: number;
  batches: Batch[];
}

type Submitter = { batch(ctx: unknown, indexes: ArrayLike<number>, vertices: ArrayLike<number>, colors: ArrayLike<number>, lighting: unknown): void };
type Renderer = (renderer: unknown, src: Phaser.GameObjects.Graphics, ctx: { camera: Phaser.Cameras.Scene2D.Camera; useCanvas?: boolean }, parent?: Phaser.GameObjects.Components.TransformMatrix) => void;
type GraphicsInternals = Phaser.GameObjects.Graphics & {
  __fkTess?: Tess;
  customRenderNodes: Record<string, unknown>;
  defaultRenderNodes: Record<string, unknown>;
  lighting?: unknown;
};

let installed = false;
/** Draws built afresh vs handed over again (tests and the bench). */
export const tessStats = { built: 0, reused: 0, shifted: 0 };

const sameCmds = (a: unknown[], b: unknown[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

/** Install the cache on every Graphics (once; WebGL only). */
export function installGraphicsCache(): void {
  if (installed) return;
  installed = true;
  const proto = Phaser.GameObjects.Graphics.prototype as unknown as { renderWebGL: Renderer };
  const build = proto.renderWebGL;
  if (!build) return;
  const getCalc = Phaser.GameObjects.GetCalcMatrix as unknown as (src: unknown, camera: unknown, parent: unknown, ignoreCameraPosition: boolean) => { calc: Phaser.GameObjects.Components.TransformMatrix };
  const scratch = { v: [] as number[] };

  proto.renderWebGL = function (renderer, src, ctx, parent) {
    const g = src as GraphicsInternals;
    const cmds = g.commandBuffer as unknown[];
    if (cmds.length === 0) return;
    const submitter = (g.customRenderNodes.Submitter ?? g.defaultRenderNodes.Submitter) as Submitter;
    const camera = ctx.camera;
    // the transform the commands are drawn with (Phaser's own, read before it builds anything)
    const m = getCalc(g, camera, parent, !ctx.useCanvas).calc.matrix;
    const a = m[0]!, b = m[1]!, c = m[2]!, d = m[3]!, e = m[4]!, f = m[5]!;
    const detail = g.pathDetailThreshold;
    const t = g.__fkTess;
    if (t && t.camera === camera && t.alpha === g.alpha && t.lighting === g.lighting && t.detail === detail && t.a === a && t.b === b && t.c === c && t.d === d && sameCmds(t.cmds, cmds)) {
      camera.addToRenderList(g);
      if (t.e === e && t.f === f) {
        tessStats.reused++;
        for (const x of t.batches) submitter.batch(ctx, x.indexes, x.vertices, x.colors, x.lighting);
        return;
      }
      // the same shapes at the same zoom, panned: every vertex moves by the pan
      tessStats.shifted++;
      const dx = e - t.e, dy = f - t.f, v = scratch.v;
      for (const x of t.batches) {
        const src = x.vertices, n = src.length;
        v.length = n;
        for (let i = 0; i < n; i += 2) { v[i] = src[i]! + dx; v[i + 1] = src[i + 1]! + dy; }
        submitter.batch(ctx, x.indexes, v, x.colors, x.lighting);
      }
      return;
    }
    // build afresh, keeping what the batch is handed (consecutive batches of one lighting merge:
    // the batch writes each index's vertex in turn, so one call writes what the run of calls wrote)
    tessStats.built++;
    const batches: Batch[] = [];
    const rec: Submitter = {
      batch(cx, indexes, vertices, colors, lighting) {
        let last = batches[batches.length - 1];
        if (!last || last.lighting !== lighting) { last = { indexes: [], vertices: [], colors: [], lighting }; batches.push(last); }
        const base = last.vertices.length / 2;
        let top = 0;
        for (let i = 0; i < indexes.length; i++) { const k = indexes[i]!; last.indexes.push(base + k); if (k + 1 > top) top = k + 1; }
        for (let i = 0; i < top * 2; i++) last.vertices.push(vertices[i]!);
        for (let i = 0; i < top; i++) last.colors.push(colors[i]!);
        submitter.batch(cx, indexes, vertices, colors, lighting);
      },
    };
    const had = Object.prototype.hasOwnProperty.call(g.customRenderNodes, 'Submitter');
    const before = g.customRenderNodes.Submitter;
    g.customRenderNodes.Submitter = rec;
    try { build.call(this, renderer, src, ctx, parent); } finally { if (had) g.customRenderNodes.Submitter = before; else delete g.customRenderNodes.Submitter; }
    g.__fkTess = { cmds: cmds.slice(), a, b, c, d, e, f, alpha: g.alpha, camera, lighting: g.lighting, detail, batches };
  };
}
